import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import {
  ClinicGroupFranchiseDto,
  ClinicGroupFranchiseSsoinfoDto,
  StackDto,
  TokenDto,
} from '../shared/models/franchise/franchise.model';

import { environment } from '../../environments/environment';
import { APIRoutesConfig } from '../api-routes.config';
import { Identifier } from '../shared/models/storage.models';
import { BeforeUnloadBypassService } from '../shared/services/before-unload-bypass.service';
import { HelpersService } from '../shared/utils/helpers/helpers.service';
import { authenticateUser, initUser, unauthenticateUser } from '../store/user/user.actions';
import { selectUser } from '../store/user/user.selectors';
import { LoginSyncService } from './login-sync.service';
import { MsalConfig } from './msal.config';

@Injectable({ providedIn: 'root' })
export class InitialStartupService {
  private tokenExpirationTimer: any;
  hardcodedWebUrl = environment.stack;
  private http: HttpClient;
  public isSSOLogin = false;

  constructor(
    private readonly httpHandler: HttpBackend,
    private router: Router,
    private helper: HelpersService,
    private store: Store<any>,
    private sync: LoginSyncService,
    private bypass: BeforeUnloadBypassService
  ) {
    this.http = new HttpClient(httpHandler);
  }

  /**
   * Restores a Central Office session from localStorage at bootstrap, and re-arms its expiry
   * timer so a restored session still ends by itself rather than rotting into 401s.
   *
   * `initUser()` is the only dispatch that reads the persisted session, and `UserEffects`
   * answers it one of two ways — both of them load-bearing: `authenticateUser` when the stored
   * token is still in date, and `unauthenticateUser` when it is missing or expired, which is
   * what *deletes* the dead session from storage.
   *
   * This used to return an observable nobody subscribed, so the `autoLogout` re-arm never ran.
   * It is now a plain side effect, because that is all it ever was.
   */
  autoLogin(): void {
    this.store.dispatch(initUser());
    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe((user) => {
        if (this.helper.userIsAuthenticated(user)) {
          this.autoLogout(this.helper.getExpirationDuration(user));
        }
      });
  }

  /**
   * Ends the Central Office session **without navigating**: stops the expiry timer, resets the
   * user slice (which `UserEffects.removeUser` mirrors out of localStorage) and tells the other
   * tabs.
   *
   * Split out of `logout()` for the SSO path. A Central Office session and the MSAL/Azure AD
   * session are cleared by two different mechanisms, and `redirectToAuth()` tears the document
   * down — so the SSO path has to clear one, wait for the other, and only then navigate. See
   * `SsoLogoutService`.
   */
  clearSession(preventBroadCastLogout = false) {
    this.clearTimer();
    this.store.dispatch(unauthenticateUser());
    if (!preventBroadCastLogout) {
      this.sync.broadcastLogout();
    }
  }

  /** Full document reload onto `/auth`, bypassing the unsaved-changes guard. */
  redirectToAuth() {
    this.bypass.enable();
    window.location.replace('/auth');
  }

  logout(preventBroadCastLogout = false) {
    this.clearSession(preventBroadCastLogout);
    this.redirectToAuth();
  }

  clearTimer() {
    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
    }
    this.tokenExpirationTimer = null;
  }

  autoLogout(expirationDuration: number) {
    this.clearTimer();
    this.tokenExpirationTimer = setTimeout(() => {
      this.logout();
    }, expirationDuration);
  }
  handleAuthentication(tokenDto: TokenDto) {
    this.store.dispatch(authenticateUser(tokenDto));
    this.autoLogout(this.helper.getExpirationDuration(tokenDto));
  }
  private getDomainName(): string {
    const domains = location.hostname.split('.');
    if (domains[0] === '127') {
      return this.hardcodedWebUrl;
    }
    if (domains && domains.length > 1) {
      if (domains[0] === 'dev10') {
        return this.hardcodedWebUrl;
      }
      return window.location.host;
    } else {
      return this.hardcodedWebUrl;
    }
  }

  public initApplication() {
    return new Promise<void>((resolve, reject) => {
      const subDomain = this.getDomainName();
      const headers = new HttpHeaders()
        .append('Content-Type', 'application/json')
        .append('Access-Control-Allow-Origin', '*')
        .append('Accept-Language', 'en-CA');

      let params = new HttpParams();
      params = params.append('portalUrl', subDomain);
      const url = `${APIRoutesConfig.coApiUrl}centraloffice/token/portalUrl`;
      return this.http
        .get<StackDto>(url, { observe: 'response', params, headers })
        .pipe(map((response) => response.body))
        .pipe(
          switchMap((backendConfig) => {
            if (!backendConfig) {
              return of(null);
            }

            this.initBackendConfigs(backendConfig);

            const headers = new HttpHeaders()
              .append('Content-Type', 'application/json')
              .append('Access-Control-Allow-Origin', '*')
              .append('Accept-Language', 'en-CA')
              .append('ClinicGroupFranchiseId', backendConfig.clinicGroupFranchiseId.toString() || '');
            const url = `${APIRoutesConfig.coApiUrl}clinicgroupfranchises/${backendConfig?.clinicGroupFranchiseId}`;
            return this.http
              .get<ClinicGroupFranchiseDto>(url, { observe: 'response', headers })
              .pipe(map((response) => response.body));
          })
        )
        .subscribe((franchise) => {
          if (franchise) {
            localStorage.setItem(Identifier.TYPE_FRANCHISE_ID, franchise.clinicGroupFranchiseId.toString());
            localStorage.setItem(
              Identifier.TYPE_OTHER_SUPPORTED_LANGUAGES,
              JSON.stringify(franchise.otherSupportedLanguages)
            );

            const ssoInfo = franchise.ssoInfo?.find((s) => s.sysSsotypeId === 3); //for web=3

            if (ssoInfo) {
              this.initSSOConfigs(ssoInfo);
            }

            // Unconditional, SSO included. When this sat in the `else` branch, an SSO franchise
            // never dispatched `initUser()`: the `user` slice stayed empty for the whole
            // bootstrap AND the previous session's `staffInfo` was never purged from
            // localStorage. `AppComponent` read that stale key as "a session exists" and issued
            // `getClinicApplicationSetup()` with an empty Authorization header, and
            // `ErrorInterceptor` turned the resulting 401 into a credentials-failure popup on a
            // portal that has no password field.
            this.autoLogin();
            resolve();
          }
        });
    });
  }

  private initSSOConfigs(ssoInfo: ClinicGroupFranchiseSsoinfoDto): void {
    this.isSSOLogin = ssoInfo && ssoInfo.sysSsotypeId === 3; //web
    const hostName = window.location.hostname === 'localhost' ? 'localhost:4200' : window.location.hostname;
    MsalConfig.isEnabled = true;
    MsalConfig.redirectUri = 'https://' + hostName;
    MsalConfig.clientId = ssoInfo.clientId;
    MsalConfig.cmApiRequest = [`api://${ssoInfo.apiClientId}/access_as_user`];
    MsalConfig.authority = `${ssoInfo.providerUri}${ssoInfo.tenantId}`;
    MsalConfig.authSchema = ssoInfo.authSchema;
    localStorage.setItem(Identifier.TYPE_IS_SSO_LOGIN, this.isSSOLogin.toString());
  }

  private initBackendConfigs(beModel: StackDto): void {
    if (beModel) {
      sessionStorage.setItem(Identifier.TYPE_COAPI_URL, beModel.centralOfficeApiUrl);
      sessionStorage.setItem(Identifier.TYPE_CMAPI_URL, beModel.monitoringWebApiBaseUri);
      sessionStorage.setItem(Identifier.TYPE_SETTINGS_URL, beModel.clinicMasterSettingsUrl);
      sessionStorage.setItem(Identifier.TYPE_FILEAPI_URL, beModel.fileApiUrl);
      sessionStorage.setItem(Identifier.TYPE_REPORTAPI_URL, beModel.reportApiUrl);
      sessionStorage.setItem(Identifier.TYPE_SIGNALRAPI_URL, beModel.signalRwebApiBaseUri);
      sessionStorage.setItem(Identifier.TYPE_SSOAPI_URL, beModel.ssoapiUrl);
      sessionStorage.setItem(Identifier.TYPE_GRAPTHAPI_URL, beModel.addaGraphApiUrl);
      sessionStorage.setItem(Identifier.TYPE_FORMIO_PROJECT_URL, beModel.formioProjectUrl);
      sessionStorage.setItem(Identifier.TYPE_CNESST_URL, beModel.cnesstIntegrationApiUrl);
    }
  }
}
